import React, { useState, useEffect } from 'react';
import apiService from '../services/apiService';
import AIDisclaimer from './AIDisclaimer';

function QuizModal({ prefill = {}, onClose }) {
  // State for all form fields, initialized from prefill
  const [quizName, setQuizName] = useState(prefill.quizName || '');
  const [selectedCourse, setSelectedCourse] = useState(prefill.course || '');
  const [selectedLesson, setSelectedLesson] = useState(prefill.lesson || '');
  const [criteria, setCriteria] = useState(prefill.criteria || '');
  const [cognitiveLevels, setCognitiveLevels] = useState(prefill.cognitiveLevels || []);
  const [difficulty, setDifficulty] = useState(prefill.difficulty || '');
  const [outcomes, setOutcomes] = useState(prefill.outcomes || []);
  const [numQuestions, setNumQuestions] = useState(prefill.numQuestions || 10);
  const [courses, setCourses] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [lessonHasDocSource, setLessonHasDocSource] = useState(true);
  const [docSourceWarning, setDocSourceWarning] = useState("");

  useEffect(() => {
    // Fetch courses for dropdown
    apiService.getCourses().then(data => setCourses(data));
  }, []);

  useEffect(() => {
    if (selectedCourse) {
      apiService.getLessonsByCourse(selectedCourse)
        .then(data => setLessons(data));
    }
  }, [selectedCourse]);

  useEffect(() => {
    if (selectedLesson && lessons.length) {
      const lesson = lessons.find(l => l.id == selectedLesson);
      if (lesson && !lesson.docsource_id) {
        setLessonHasDocSource(false);
        setDocSourceWarning(
          "This lesson has not been transcribed or ingested yet. Please transcribe (for video) or ingest (for PDF) before generating a question set."
        );
      } else {
        setLessonHasDocSource(true);
        setDocSourceWarning("");
      }
    } else {
      setLessonHasDocSource(true);
      setDocSourceWarning("");
    }
  }, [selectedLesson, lessons]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      // Build payload for backend
      const payload = {
        lesson_id: selectedLesson,
        answer_style: criteria,
        n: numQuestions,
      };
      if (cognitiveLevels.length) payload.taxonomy_levels = cognitiveLevels;
      // Add more fields as needed
      const data = await apiService.generateQuestionSet(payload);
      setResult(data);
    } catch (err) {
      setError(err.message || 'Failed to generate question set.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <button className="modal-close" onClick={onClose}>×</button>
        <h2>Create New Question Set</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Question Set Name</label>
            <input value={quizName} onChange={e => setQuizName(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Select Course</label>
            <select value={selectedCourse} onChange={e => setSelectedCourse(e.target.value)}>
              <option value="">-- Choose a course --</option>
              {courses.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Select Content Source (Lesson)</label>
            <select value={selectedLesson} onChange={e => setSelectedLesson(e.target.value)}>
              <option value="">-- Choose a lesson --</option>
              {lessons.map(l => <option key={l.id} value={l.id}>{l.title}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Generation Criteria</label>
            <select value={criteria} onChange={e => setCriteria(e.target.value)}>
              <option value="">-- Choose criteria --</option>
              <option value="mcq">Cognitive Level</option>
              <option value="difficulty">Difficulty Level</option>
              <option value="outcomes">Outcomes</option>
            </select>
          </div>
          {/* Render further options based on criteria */}
          {criteria === 'mcq' && (
            <div className="form-group">
              <label>Cognitive Levels</label>
              <label><input type="checkbox" value="remember" checked={cognitiveLevels.includes('remember')} onChange={e => setCognitiveLevels(l => e.target.checked ? [...l, 'remember'] : l.filter(x => x !== 'remember'))} /> Remember</label>
              <label><input type="checkbox" value="understand" checked={cognitiveLevels.includes('understand')} onChange={e => setCognitiveLevels(l => e.target.checked ? [...l, 'understand'] : l.filter(x => x !== 'understand'))} /> Understand</label>
              <label><input type="checkbox" value="apply" checked={cognitiveLevels.includes('apply')} onChange={e => setCognitiveLevels(l => e.target.checked ? [...l, 'apply'] : l.filter(x => x !== 'apply'))} /> Apply</label>
            </div>
          )}
          {criteria === 'difficulty' && (
            <div className="form-group">
              <label>Difficulty</label>
              <select value={difficulty} onChange={e => setDifficulty(e.target.value)}>
                <option value="">-- Choose difficulty --</option>
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
                <option value="mixed">Mixed</option>
              </select>
            </div>
          )}
          {/* Add outcomes selection if needed */}
          <div className="form-group">
            <label>Number of Questions</label>
            <input type="number" value={numQuestions} onChange={e => setNumQuestions(e.target.value)} min={1} max={50} />
          </div>
          <button className="btn btn-primary" type="submit" disabled={loading || !lessonHasDocSource}>{loading ? 'Generating...' : '✨ Generate Question Set'}</button>
        </form>
        {docSourceWarning && <div className="alert alert-warning" style={{marginTop:8}}>{docSourceWarning}</div>}
        {error && <div className="alert alert-danger">{error}</div>}
        {result && (
          <div className="quiz-preview">
            <AIDisclaimer message="These questions were generated by AI. Please review and verify accuracy before using them." />
            <h3>Generated Questions</h3>
            {result.questions.map((q, i) => (
              <div key={i} className="question-card">
                <div className="question-number">Q{i + 1}</div>
                <div className="question-text">{q.text}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default QuizModal; 